import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from 'model/Api';
import { WorkProcess } from 'model/WorkProcess';
import { generalStyle } from 'helper/Style';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import OperationTrigger from 'component/part/OperationTrigger';
import H2 from 'component/part/H2';

type SortStatus = {
  draggIndex: number | null;
  dropIndex: number | null;
};

const style = {
  form: {
    ...generalStyle.form,
    gap: '0',
    width: '50rem',
  },
  formRow: {
    ...generalStyle.formRow,
    display: 'grid',
    gridTemplateColumns: 'auto auto 1fr auto',
    alignItems: 'end',
    gap: '1rem',
    padding: '0.8rem 0 1.2rem',
    ['&:nth-of-type(1)']: {
      paddingTop: '0',
    },
    ['&:nth-of-last-type(1)']: {
      paddingBottom: '0',
    },
    ['& > div:nth-of-type(1)']: {
      width: '1.6rem',
      height: '3rem',
      backgroundColor: '#aaaacc',
      cursor: 'all-scroll',
    },
  },
  sortTarget: {
    paddingBottom: '0.8rem',
    borderBottom: 'solid 0.2rem #aaaacc',
  },
  addButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'fit-content',
    padding: '0 1rem',
    height: '3rem',
    backgroundColor: '#eeeeee',
    border: 'solid 0.1rem #aaaaaa',
    borderRadius: '0.3rem',
    cursor: 'pointer',
    ['&:hover']: {
      backgroundColor: '#cccccc',
    },
  },
};

const WorkProcessEdit: React.FC = () => {
  const api = useApi();
    const navigate = useNavigate();
  const [workProcesses, setWorkProcesses] = useState<WorkProcess[]>([]);
  const [originalWorkProcesses, setOriginalWorkProcesses] = useState<WorkProcess[]>([]);
  const [deletedWorkProcessIds, setDeletedWorkProcessIds] = useState<number[]>([]);
  const [sortStatus, setSortStatus] = useState<SortStatus>({ draggIndex: null, dropIndex: null });

  const onAddWorkProcess = (index: number) => {
    const blankItem: WorkProcess = {
      id: 0,
      name: '',
      phase: 0,
    };
    workProcesses.splice(index, 0, blankItem);
    setWorkProcesses([...workProcesses]);
  };

  const onChangeWorkProcess = <T extends keyof WorkProcess>(
    workProcess: WorkProcess,
    key: T,
    value: WorkProcess[T]
  ) => {
    if (workProcesses) {
      workProcess[key] = value;
      setWorkProcesses([...workProcesses]);
    }
  };

  const onDeleteWorkProcess = (index: number) => {
    const { id } = workProcesses[index];
    workProcesses.splice(index, 1);
    setWorkProcesses([...workProcesses]);
    setDeletedWorkProcessIds([...deletedWorkProcessIds, id]);
  };

  const onStartDragg = (index: number) => {
    setSortStatus({ ...sortStatus, draggIndex: index });
  };

  const onOverDragg = (index: number, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setSortStatus({ ...sortStatus, dropIndex: index });
  };

  const onEndDragg = (index: number | null, event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if ((sortStatus.draggIndex ?? index) !== null && sortStatus.dropIndex !== null) {
      const draggIndex = (sortStatus.draggIndex ?? index) as number;
      const dropIndex = sortStatus.dropIndex + (draggIndex > sortStatus.dropIndex ? 1 : 0);
      const [workProcess] = workProcesses.splice(draggIndex, 1);
      workProcesses.splice(dropIndex, 0, workProcess);
      setWorkProcesses([...workProcesses]);
    }
    setSortStatus({ draggIndex: null, dropIndex: null });
  };

  const onSubmit = async () => {
    const [createdWorkProcesses, updatedWorkProcesses]: WorkProcess[][] = [[], []];
    workProcesses.forEach((item, index) => {
      item.phase = index + 1;
      if (item.id) {
        const originalItem = originalWorkProcesses.find(({ id }) => id === item.id);
        const isUpdated = (['name', 'phase'] as (keyof WorkProcess)[]).reduce(
          (judge, key) => judge || (originalItem?.[key] !== item[key]),
          false,
        );
        if (isUpdated) {
          updatedWorkProcesses.push(item);
        }
      } else {
        createdWorkProcesses.push(item);
      }
    });

    return !!(await api.workProcess.updates({
      createList: createdWorkProcesses,
      updateList: updatedWorkProcesses,
      deleteList: deletedWorkProcessIds,
    }));
  };

  useEffect(() => {
    (async () => {
      const workProcessesResponse = await api.workProcess.getsByOwn();
      setOriginalWorkProcesses(workProcessesResponse);
      setWorkProcesses(workProcessesResponse.map((item) => ({ ...item })));
    })();
  }, []);

  return <Layout>
    <BackLink link={`/work-process/list`} label='作業内容一覧'></BackLink>
    <H2>作業内容編集</H2>
    <div css={generalStyle.form}>
      <div css={style.form}>
        {workProcesses.map((workProcess, index) => (
          <div
            draggable={true}
            onDragStart={() => onStartDragg(index)}
            onDragOver={(event) => onOverDragg(index, event)}
            onDragEnd={(event) => onEndDragg(index, event)}
            onDrop={(event) => onEndDragg(null, event)}
            css={[
              style.formRow,
              ...(index === sortStatus.dropIndex ? [style.sortTarget] : [])
            ]}
            key={index}
          >
            <div />
            <button onClick={() => onDeleteWorkProcess(index)} css={style.addButton}>ー</button>
            <div>
              <FormContent label='作業内容'>
                <input
                  type='text'
                  value={workProcess.name}
                  onChange={(event) => onChangeWorkProcess(workProcess, 'name', event.target.value)}
                />
              </FormContent>
            </div>
            <button onClick={() => onAddWorkProcess(index)} css={style.addButton}>
              <img src={'/image/icon/arrow-up-left.svg'} />上に追加
            </button>
          </div>
        ))}
      </div>
      <div css={generalStyle.formRow}>
        <button onClick={() => onAddWorkProcess(workProcesses.length)} css={style.addButton}>＋追加</button>
      </div>
      <OperationTrigger
        onSubmit={onSubmit}
        onOk={() => navigate('/work-process/list')}
        buttonText='更新'
        confirmText='変更内容を確定します'
        submitText='更新'
        doneText='更新が完了しました'
        errorText='更新に失敗しました'
      />
    </div>
  </Layout>;
};

export default WorkProcessEdit;
